/**
 * CON-1487 — Event Templates client constants.
 *
 * Shape and values mirror `eventTemplatesService.js` on the server. Keep
 * these in sync if the enum ever changes.
 */

export const TEMPLATE_TYPES = {
  INVITE_SMS_HOST_INDIVIDUAL: 'invite_sms_host_individual',
  INVITE_OTHER_APP_HOST_INDIVIDUAL: 'invite_other_app_host_individual',
  INVITE_SMS_CONSULTANT_GROUP: 'invite_sms_consultant_group',
  INVITE_EMAIL: 'invite_email_host_individual',
  REMINDER_TO_JOIN_SMS: 'reminder_to_join_sms_host_individual',
  REMINDER_TO_JOIN_OTHER_APP: 'reminder_to_join_other_app_host_individual',
  REMINDER_TO_JOIN_EMAIL: 'reminder_to_join_email_host_individual',
  REMINDER_TO_ORDER_SMS: 'reminder_to_order_sms_host_individual',
  REMINDER_TO_ORDER_OTHER_APP: 'reminder_to_order_other_app_host_individual',
  REMINDER_TO_ORDER_EMAIL: 'reminder_to_order_email_host_individual',
  THANKS_FOR_ORDERING_SMS: 'thanks_for_ordering_sms_host_individual',
  THANKS_FOR_ORDERING_OTHER_APP: 'thanks_for_ordering_other_app_host_individual',
  THANKS_FOR_ORDERING_EMAIL: 'thanks_for_ordering_email_host_individual',
};

export const ACTIVE_TEMPLATE_TYPES = [
  TEMPLATE_TYPES.INVITE_SMS_HOST_INDIVIDUAL,
  TEMPLATE_TYPES.INVITE_OTHER_APP_HOST_INDIVIDUAL,
  TEMPLATE_TYPES.INVITE_SMS_CONSULTANT_GROUP,
  TEMPLATE_TYPES.INVITE_EMAIL,
  TEMPLATE_TYPES.REMINDER_TO_JOIN_SMS,
  TEMPLATE_TYPES.REMINDER_TO_JOIN_OTHER_APP,
  TEMPLATE_TYPES.REMINDER_TO_JOIN_EMAIL,
  TEMPLATE_TYPES.REMINDER_TO_ORDER_SMS,
  TEMPLATE_TYPES.REMINDER_TO_ORDER_OTHER_APP,
  TEMPLATE_TYPES.REMINDER_TO_ORDER_EMAIL,
  TEMPLATE_TYPES.THANKS_FOR_ORDERING_SMS,
  TEMPLATE_TYPES.THANKS_FOR_ORDERING_OTHER_APP,
  TEMPLATE_TYPES.THANKS_FOR_ORDERING_EMAIL,
];

export const TEMPLATE_DISPLAY_NAMES = {
  [TEMPLATE_TYPES.INVITE_SMS_HOST_INDIVIDUAL]: "Invite Text (from host's phone)",
  [TEMPLATE_TYPES.INVITE_OTHER_APP_HOST_INDIVIDUAL]: 'Invite via FB Messenger or Another App',
  [TEMPLATE_TYPES.INVITE_SMS_CONSULTANT_GROUP]: 'Invite Text (through consultant)',
  [TEMPLATE_TYPES.INVITE_EMAIL]: 'Invite Email',
  [TEMPLATE_TYPES.REMINDER_TO_JOIN_SMS]: 'Reminder to Join Text',
  [TEMPLATE_TYPES.REMINDER_TO_JOIN_OTHER_APP]: 'Reminder to Join (via FB Messenger or Another App)',
  [TEMPLATE_TYPES.REMINDER_TO_JOIN_EMAIL]: 'Reminder to Join Email',
  [TEMPLATE_TYPES.REMINDER_TO_ORDER_SMS]: 'Reminder to Order Text',
  [TEMPLATE_TYPES.REMINDER_TO_ORDER_OTHER_APP]: 'Reminder to Order (via FB Messenger or Another App)',
  [TEMPLATE_TYPES.REMINDER_TO_ORDER_EMAIL]: 'Reminder to Order Email',
  [TEMPLATE_TYPES.THANKS_FOR_ORDERING_SMS]: 'Thanks for Ordering Text',
  [TEMPLATE_TYPES.THANKS_FOR_ORDERING_OTHER_APP]: 'Thanks for Ordering (via FB Messenger or Another App)',
  [TEMPLATE_TYPES.THANKS_FOR_ORDERING_EMAIL]: 'Thanks for Ordering Email',
};

/**
 * Per-template self-documenting subtext shown beneath the Template name
 * in the list view. Matches the split described in CON-1487 Phase E #14.
 */
export const TEMPLATE_SUBTEXTS = {
  [TEMPLATE_TYPES.INVITE_SMS_HOST_INDIVIDUAL]:
    'Text that the host sends one-by-one to friends from her own phone',
  [TEMPLATE_TYPES.INVITE_OTHER_APP_HOST_INDIVIDUAL]:
    'Message that the host shares via Facebook Messenger, Instagram, WhatsApp, or another app',
  [TEMPLATE_TYPES.INVITE_SMS_CONSULTANT_GROUP]:
    "Text sent from your CP number to the host's friends",
  [TEMPLATE_TYPES.INVITE_EMAIL]:
    'Email that the host sends to friends from her own email app',
  [TEMPLATE_TYPES.REMINDER_TO_JOIN_SMS]:
    'Text that the host sends to remind friends to RSVP',
  [TEMPLATE_TYPES.REMINDER_TO_JOIN_OTHER_APP]:
    'Message that the host shares via Facebook Messenger or another app to remind friends to RSVP',
  [TEMPLATE_TYPES.REMINDER_TO_JOIN_EMAIL]:
    'Email that the host sends to remind friends to RSVP',
  [TEMPLATE_TYPES.REMINDER_TO_ORDER_SMS]:
    'Text that the host sends to remind guests to place their order',
  [TEMPLATE_TYPES.REMINDER_TO_ORDER_OTHER_APP]:
    'Message that the host shares via Facebook Messenger or another app to remind guests to place their order',
  [TEMPLATE_TYPES.REMINDER_TO_ORDER_EMAIL]:
    'Email that the host sends to remind guests to place their order',
  [TEMPLATE_TYPES.THANKS_FOR_ORDERING_SMS]:
    'Text that the host sends to thank guests for placing their order',
  [TEMPLATE_TYPES.THANKS_FOR_ORDERING_OTHER_APP]:
    'Message that the host shares via Facebook Messenger or another app to thank a guest after they order',
  [TEMPLATE_TYPES.THANKS_FOR_ORDERING_EMAIL]:
    'Email that the host sends to thank guests for placing their order',
};

export const SCOPE = {
  PARTY_TYPE: 'partyType',
  PARTY: 'party',
};

export const SOURCE = {
  USER_PARTY: 'user_party',
  USER_PARTY_TYPE: 'user_partyType',
  COMPANY_DEFAULT: 'company_default',
  GENERIC_DEFAULT: 'generic_default',
  NONE: 'none',
};

export const SOURCE_LABELS = {
  [SOURCE.USER_PARTY]: 'My template',
  [SOURCE.USER_PARTY_TYPE]: 'My template',
  [SOURCE.COMPANY_DEFAULT]: 'Default',
  [SOURCE.GENERIC_DEFAULT]: 'Default',
  [SOURCE.NONE]: 'Default',
};

export const EVENT_TEMPLATE_SCREEN_NAME = 'Event Template';

export const isEmailTemplateType = (templateType) => (
  typeof templateType === 'string' && templateType.includes('_email')
);

export const isSmsTemplateType = (templateType) => (
  typeof templateType === 'string' && templateType.includes('_sms')
);

export const isOtherAppTemplateType = (templateType) => (
  typeof templateType === 'string' && templateType.includes('_other_app_')
);

/**
 * Host-send SMS templates: the four *_sms_host_individual types. The host
 * sends these one-by-one from her own phone, so a body with no
 * contact-scoped token produces a byte-identical batch.
 *
 * Deliberately EXCLUDES invite_sms_consultant_group: CP dispatches that one
 * per contact through the scheduled-send pipeline, where contact-scoped
 * tokens are resolved at send time.
 */
export const isHostSendSmsTemplateType = (templateType) => (
  typeof templateType === 'string' && templateType.endsWith('_sms_host_individual')
);

export const isUserSource = (source) => (
  source === SOURCE.USER_PARTY || source === SOURCE.USER_PARTY_TYPE
);

/**
 * Attachment support policy.
 *
 * The consultant-group SMS template uses server-scheduled Lumian MMS.
 * The other six active templates were added in CON-1512 once
 * cp-event-app adopted expo-sms / expo-mail-composer, which can
 * attach images natively at the Messages / Mail composer level.
 * Reminder send paths in cp-event-app share the same editor +
 * smsLoop screens as invites, so they get attachment support for
 * free via the same expo-sms / expo-mail-composer pipeline.
 */
export const TEMPLATE_TYPES_WITH_ATTACHMENTS = new Set([
  TEMPLATE_TYPES.INVITE_SMS_HOST_INDIVIDUAL,
  TEMPLATE_TYPES.INVITE_OTHER_APP_HOST_INDIVIDUAL,
  TEMPLATE_TYPES.INVITE_SMS_CONSULTANT_GROUP,
  TEMPLATE_TYPES.INVITE_EMAIL,
  TEMPLATE_TYPES.REMINDER_TO_JOIN_SMS,
  TEMPLATE_TYPES.REMINDER_TO_JOIN_OTHER_APP,
  TEMPLATE_TYPES.REMINDER_TO_JOIN_EMAIL,
  TEMPLATE_TYPES.REMINDER_TO_ORDER_SMS,
  TEMPLATE_TYPES.REMINDER_TO_ORDER_OTHER_APP,
  TEMPLATE_TYPES.REMINDER_TO_ORDER_EMAIL,
  TEMPLATE_TYPES.THANKS_FOR_ORDERING_SMS,
  TEMPLATE_TYPES.THANKS_FOR_ORDERING_OTHER_APP,
  TEMPLATE_TYPES.THANKS_FOR_ORDERING_EMAIL,
]);

export const supportsAttachment = (templateType) => (
  TEMPLATE_TYPES_WITH_ATTACHMENTS.has(templateType)
);

/**
 * Contact-card attachment policy.
 *
 * Only the consultant-group SMS template supports attaching the
 * consultant's vCard — it ships via server-scheduled Lumian MMS.
 * The host-send templates use the native Messages / Mail composer
 * which doesn't have a meaningful "attach my contact card" path,
 * and event-template attachments are images-only by policy.
 */
export const TEMPLATE_TYPES_WITH_CONTACT_CARD_OPTION = new Set([
  TEMPLATE_TYPES.INVITE_SMS_CONSULTANT_GROUP,
]);

export const supportsContactCardOption = (templateType) => (
  TEMPLATE_TYPES_WITH_CONTACT_CARD_OPTION.has(templateType)
);

/**
 * Three-toggle send methods — backing field names on parties / partyTypes.
 * The SendMethodsCard reads these values from the partyType/party Redux
 * slice and writes them via the combined PATCH /send-methods endpoint
 * (party scope) or the extended PUT /partytypes endpoint (partyType
 * scope, with cascade flag).
 */
export const SEND_METHOD_FIELDS = {
  CONSULTANT_TEXT: 'hostCanInviteViaConsultantSMS',
  HOST_TEXTING: 'hostHasTextingOptions',
  HOST_EMAIL: 'hostHasEmailOptions',
  OTHER_APP: 'hostCanInviteViaOtherApp',
};

/**
 * Maps each greyable templateType to the send-method field that gates it.
 * Used by EventTemplatesListView's row-disable logic so adding a new
 * template type or a new toggle is a single-line change here.
 */
export const TEMPLATE_DISABLE_MAP = {
  [TEMPLATE_TYPES.INVITE_SMS_CONSULTANT_GROUP]: SEND_METHOD_FIELDS.CONSULTANT_TEXT,
  [TEMPLATE_TYPES.INVITE_SMS_HOST_INDIVIDUAL]: SEND_METHOD_FIELDS.HOST_TEXTING,
  // The four other-app rows are gated by the fourth toggle, decoupled from
  // HOST_TEXTING — mirrors the backend gateField re-point to OTHER_APP
  // (migration 20260607120000).
  [TEMPLATE_TYPES.INVITE_OTHER_APP_HOST_INDIVIDUAL]: SEND_METHOD_FIELDS.OTHER_APP,
  [TEMPLATE_TYPES.REMINDER_TO_JOIN_SMS]: SEND_METHOD_FIELDS.HOST_TEXTING,
  [TEMPLATE_TYPES.REMINDER_TO_JOIN_OTHER_APP]: SEND_METHOD_FIELDS.OTHER_APP,
  [TEMPLATE_TYPES.REMINDER_TO_ORDER_SMS]: SEND_METHOD_FIELDS.HOST_TEXTING,
  [TEMPLATE_TYPES.REMINDER_TO_ORDER_OTHER_APP]: SEND_METHOD_FIELDS.OTHER_APP,
  [TEMPLATE_TYPES.THANKS_FOR_ORDERING_SMS]: SEND_METHOD_FIELDS.HOST_TEXTING,
  [TEMPLATE_TYPES.THANKS_FOR_ORDERING_OTHER_APP]: SEND_METHOD_FIELDS.OTHER_APP,
  [TEMPLATE_TYPES.INVITE_EMAIL]: SEND_METHOD_FIELDS.HOST_EMAIL,
  [TEMPLATE_TYPES.REMINDER_TO_JOIN_EMAIL]: SEND_METHOD_FIELDS.HOST_EMAIL,
  [TEMPLATE_TYPES.REMINDER_TO_ORDER_EMAIL]: SEND_METHOD_FIELDS.HOST_EMAIL,
  [TEMPLATE_TYPES.THANKS_FOR_ORDERING_EMAIL]: SEND_METHOD_FIELDS.HOST_EMAIL,
};

/**
 * Tooltip copy shown on a greyed template row, keyed by the send-method
 * field that gates that row. Explains which toggle to flip ON to enable
 * the row. Quote-free for CSV / commit-message safety.
 */
export const SEND_METHOD_DISABLED_TOOLTIPS = {
  [SEND_METHOD_FIELDS.CONSULTANT_TEXT]:
    'Texting through the consultants CP number is currently disabled for this event. Turn on Host Can Invite Via Consultant Text to enable.',
  [SEND_METHOD_FIELDS.HOST_TEXTING]:
    'Texting from the hosts phone is currently disabled for this event. Turn on Host Has Texting Invite & Reminder Options to enable.',
  [SEND_METHOD_FIELDS.HOST_EMAIL]:
    'Emailing from the hosts email is currently disabled for this event. Turn on Host Has Email Invite & Reminder Options to enable.',
  [SEND_METHOD_FIELDS.OTHER_APP]:
    'Sharing to Facebook Messenger or another app is currently disabled for this event. Turn on Host Can Invite Via Facebook Messenger & Other to enable.',
};

/**
 * Display labels for the three send-method toggles.
 */
export const SEND_METHOD_LABELS = {
  [SEND_METHOD_FIELDS.CONSULTANT_TEXT]: 'Host Can Invite Via Consultant Text',
  [SEND_METHOD_FIELDS.HOST_TEXTING]: 'Host Has Texting Invite & Reminder Options',
  [SEND_METHOD_FIELDS.HOST_EMAIL]: 'Host Has Email Invite & Reminder Options',
  [SEND_METHOD_FIELDS.OTHER_APP]: 'Host Can Invite Via Facebook Messenger & Other',
};

/**
 * Permission required to view+toggle each send-method field.
 * Mirrors the constants in client/src/constants/permissions.js.
 */
export const SEND_METHOD_PERMISSIONS = {
  [SEND_METHOD_FIELDS.CONSULTANT_TEXT]: 'HOST_CAN_INVITE_VIA_CONSULTANT_SMS_VIEW',
  [SEND_METHOD_FIELDS.HOST_TEXTING]: 'HOST_HAS_TEXTING_OPTIONS_VIEW',
  [SEND_METHOD_FIELDS.HOST_EMAIL]: 'HOST_HAS_EMAIL_OPTIONS_VIEW',
  [SEND_METHOD_FIELDS.OTHER_APP]: 'HOST_CAN_INVITE_VIA_OTHER_VIEW',
};
